import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Calendar, Clock, MapPin, User, Mic, MessageCircle, Wrench, PartyPopper, Flag, Loader } from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';

const Programacao = () => {
  const [atividades, setAtividades] = useState([]);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [atividadeSelecionada, setAtividadeSelecionada] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    carregarProgramacao();
  }, []);
  
  const carregarProgramacao = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('schedule_events')
        .select('*')
        .eq('active', true)
        .order('date', { ascending: true })
        .order('start_time', { ascending: true });
      
      if (error) throw error;
      
      // Transformar os dados para o formato esperado pelo componente
      const atividadesFormatadas = data.map(item => ({
        id: item.id,
        titulo: item.title,
        descricao: item.description,
        data: item.date,
        horario: formatarHorarioSupabase(item.start_time),
        duracao: calcularDuracao(item.start_time, item.end_time),
        local: item.location,
        palestrante: item.speaker,
        tipo: item.event_type || 'palestra'
      }));
      
      setAtividades(atividadesFormatadas);
    } catch (error) {
      console.error('Erro ao carregar programação:', error);
      toast.error('Erro ao carregar a programação. Tente novamente mais tarde.');
    } finally {
      setLoading(false);
    }
  };

  const tiposAtividade = [
    { value: 'todos', label: 'Todas as Atividades', icon: Calendar },
    { value: 'abertura', label: 'Abertura', icon: PartyPopper },
    { value: 'palestra', label: 'Palestras', icon: Mic },
    { value: 'miniscurso', label: 'Miniscursos', icon: MessageCircle },
    { value: 'oficina', label: 'Oficinas', icon: Wrench },
    { value: 'apresentacao', label: 'Apresentações de trabalhos', icon: User },
    { value: 'encerramento', label: 'Encerramento', icon: Flag }
  ];

  const atividadesFiltradas = filtroTipo === 'todos' 
    ? atividades 
    : atividades.filter(atividade => atividade.tipo === filtroTipo);

  const formatarData = (data) => {
    const [ano, mes, dia] = data.split('-');
    return `${dia}/${mes}/${ano}`;
  };

  const formatarHorario = (horario) => {
    return horario;
  };
  
  const formatarHorarioSupabase = (horario) => {
    if (!horario) return '--:--';
    return horario.substring(0, 5); // Formato HH:MM
  };
  
  const calcularDuracao = (inicio, fim) => {
    if (!inicio || !fim) return 'Indeterminado';
    
    const [horaInicio, minInicio] = inicio.split(':').map(Number);
    const [horaFim, minFim] = fim.split(':').map(Number);
    
    let duracaoMinutos = (horaFim * 60 + minFim) - (horaInicio * 60 + minInicio);
    if (duracaoMinutos < 0) duracaoMinutos += 24 * 60; // Se passar da meia-noite
    
    const horas = Math.floor(duracaoMinutos / 60);
    const minutos = duracaoMinutos % 60;
    
    if (horas === 0) {
      return `${minutos} minutos`;
    } else if (minutos === 0) {
      return `${horas} hora${horas > 1 ? 's' : ''}`;
    } else {
      return `${horas} hora${horas > 1 ? 's' : ''} e ${minutos} minuto${minutos > 1 ? 's' : ''}`;
    }
  };

  const getIconeTipo = (tipo) => {
    const icones = {
      'abertura': PartyPopper,
      'palestra': Mic,
      'miniscurso': MessageCircle,
      'oficina': Wrench,
      'apresentacao': User,
      'encerramento': Flag
    };
    return icones[tipo] || Calendar;
  };

  const getCorTipo = (tipo) => {
    const cores = {
      'abertura': 'from-purple-500 to-purple-600',
      'palestra': 'from-amazonia-green-500 to-amazonia-green-600',
      'miniscurso': 'from-golden-500 to-golden-600',
      'oficina': 'from-amazonia-dark-500 to-amazonia-dark-600',
      'apresentacao': 'from-blue-500 to-blue-600',
      'encerramento': 'from-red-500 to-red-600'
    };
    return cores[tipo] || 'from-gray-500 to-gray-600';
  };

  const agruparPorData = (atividades) => {
    return atividades.reduce((grupos, atividade) => {
      const data = atividade.data;
      if (!grupos[data]) {
        grupos[data] = [];
      }
      grupos[data].push(atividade);
      return grupos;
    }, {});
  };

  const atividadesAgrupadas = agruparPorData(atividadesFiltradas);

  return (
    <div className="min-h-screen py-8 w-full">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="flex items-center justify-center mb-4">
            <h1 className="text-4xl md:text-5xl font-bold text-amazonia-dark-800">
              Programação do Evento
            </h1>
          </div>
        </motion.div>

        {/* Filtros */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h3 className="text-lg font-semibold text-amazonia-dark-800 mb-4">
              Filtrar por tipo de atividade:
            </h3>
            <div className="flex flex-wrap gap-3">
              {tiposAtividade.map((tipo) => {
                const IconComponent = tipo.icon;
                return (
                  <button
                    key={tipo.value}
                    onClick={() => setFiltroTipo(tipo.value)}
                    className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
                      filtroTipo === tipo.value
                        ? 'bg-amazonia-green-600 text-white shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-amazonia-green-50 hover:text-amazonia-green-700'
                    }`}
                  >
                    <IconComponent className="w-4 h-4" />
                    <span>{tipo.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </motion.div>

        {/* Lista de Atividades */}
        <div className="space-y-8">
          {Object.entries(atividadesAgrupadas).map(([data, atividadesDoDia], index) => (
            <motion.div
              key={data}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              <div className="bg-amazonia-green-600 text-white px-6 py-3 rounded-t-xl">
                <h2 className="text-xl font-bold flex items-center space-x-2">
                  <Calendar className="w-5 h-5" />
                  <span>{formatarData(data)}</span>
                </h2>
              </div>
              
              <div className="bg-white rounded-b-xl shadow-lg overflow-hidden">
                {atividadesDoDia
                  .sort((a, b) => a.horario.localeCompare(b.horario))
                  .map((atividade, atividadeIndex) => (
                    <motion.div
                      key={atividade.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: (index * 0.1) + (atividadeIndex * 0.05) }}
                      className="border-b border-gray-100 last:border-b-0 hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setAtividadeSelecionada(atividade)}
                    >
                      <div className="p-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <div className={`w-10 h-10 bg-gradient-to-r ${getCorTipo(atividade.tipo)} rounded-full flex items-center justify-center text-white`}>
                                {React.createElement(getIconeTipo(atividade.tipo), { className: 'w-5 h-5' })}
                              </div>
                              <div>
                                <h3 className="text-lg font-semibold text-amazonia-dark-800">
                                  {atividade.titulo}
                                </h3>
                                <p className="text-sm text-gray-600">
                                  {atividade.palestrante}
                                </p>
                              </div>
                            </div>
                            
                            <p className="text-gray-700 mb-3 line-clamp-2">
                              {atividade.descricao}
                            </p>
                            
                            <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                              <span className="flex items-center space-x-1">
                                <Clock className="w-4 h-4" />
                                <span>{formatarHorario(atividade.horario)}</span>
                              </span>
                              <span className="flex items-center space-x-1">
                                <MapPin className="w-4 h-4" />
                                <span>{atividade.local}</span>
                              </span>
                              <span className="flex items-center space-x-1">
                                <Clock className="w-4 h-4" />
                                <span>{atividade.duracao}</span>
                              </span>
                            </div>
                          </div>
                          
                          <div className="mt-4 md:mt-0 md:ml-6">
                            <button className="bg-amazonia-green-600 hover:bg-amazonia-green-700 text-white px-4 py-2 rounded-lg transition-colors text-sm font-medium">
                              Ver Detalhes
                            </button>
                          </div>
                        </div>
                      </div>
                    </motion.div>
                  ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Modal de Detalhes */}
        {atividadeSelecionada && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setAtividadeSelecionada(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className={`bg-gradient-to-r ${getCorTipo(atividadeSelecionada.tipo)} text-white p-6 rounded-t-xl`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
                      {React.createElement(getIconeTipo(atividadeSelecionada.tipo), { className: 'w-6 h-6 text-white' })}
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold">
                        {atividadeSelecionada.titulo}
                      </h2>
                      <p className="text-white text-opacity-90">
                        {atividadeSelecionada.palestrante}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => setAtividadeSelecionada(null)}
                    className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-full transition-colors"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 text-gray-600 mb-1">
                      <Calendar className="w-4 h-4" />
                      <span className="font-medium">Data</span>
                    </div>
                    <p className="text-amazonia-dark-800 font-semibold">
                      {formatarData(atividadeSelecionada.data)}
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 text-gray-600 mb-1">
                      <Clock className="w-4 h-4" />
                      <span className="font-medium">Horário</span>
                    </div>
                    <p className="text-amazonia-dark-800 font-semibold">
                      {formatarHorario(atividadeSelecionada.horario)}
                    </p>
                  </div>
                  
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center space-x-2 text-gray-600 mb-1">
                      <Clock className="w-4 h-4" />
                      <span className="font-medium">Duração</span>
                    </div>
                    <p className="text-amazonia-dark-800 font-semibold">
                      {atividadeSelecionada.duracao}
                    </p>
                  </div>
                </div>
                
                <div className="mb-6">
                  <div className="flex items-center space-x-2 text-gray-600 mb-2">
                    <MapPin className="w-4 h-4" />
                    <span className="font-medium">Local</span>
                  </div>
                  <p className="text-amazonia-dark-800 font-semibold text-lg">
                    {atividadeSelecionada.local}
                  </p>
                </div>
                
                <div>
                  <h3 className="font-semibold text-amazonia-dark-800 mb-3 text-lg">
                    Descrição da Atividade
                  </h3>
                  <p className="text-gray-700 leading-relaxed">
                    {atividadeSelecionada.descricao}
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Estado de carregamento */}
        {loading && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="flex justify-center mb-4">
              <Loader className="w-12 h-12 text-amazonia-green-600 animate-spin" />
            </div>
            <h3 className="text-xl font-semibold text-gray-600">
              Carregando programação...
            </h3>
          </motion.div>
        )}
        
        {/* Mensagem quando não há atividades */}
        {!loading && atividadesFiltradas.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="text-6xl mb-4">📅</div>
            <h3 className="text-xl font-semibold text-gray-600 mb-2">
              Nenhuma atividade encontrada
            </h3>
            <p className="text-gray-500">
              Tente selecionar um filtro diferente para ver mais atividades.
            </p>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default Programacao;
